import { Component, ViewChild, HostListener, ChangeDetectorRef, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { SimpleSearchComponent } from 'src/app/base/components/abstract/simple/simple-search.component';
import { DataAction } from 'src/app/base/enum/data-action.enum';
import { DialogReturnData } from 'src/app/base/models/dialog-return-data.model';
import { Option } from 'src/app/base/models/option.model';
import { TableColumn } from 'src/app/base/models/table-column.model';
import { takeUntil, Subject, switchMap, filter, lastValueFrom, take } from 'rxjs';
import { StockSummaryService } from '../../services/stock-summary.service';
import { CommonService } from '../../services/common.service';
import { CustomerOrderInquiryService } from '../../services/customer-order-inquiry.service';
import { CurrencyOption } from '../../models/customer-order-inquiry.model';
import { UcInqByPartNoService } from '../../services/uc-inq-by-part-no.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { Table } from 'primeng/table';

interface ItemOption extends Option {
  description?: string;
  brand?: string;
}

@Component({
  selector: 'app-uc-inq-by-part-no',
  templateUrl: './uc-inq-by-part-no.component.html',
  styleUrls: ['./uc-inq-by-part-no.component.scss'],
})
export class UcInqByPartNoComponent extends SimpleSearchComponent {
  override rowDataKeyName: string = 'ouId';
  override rowDataDeleteHintFieldName: string = 'ouId';
  override componentClassName: string = 'UcInqByPartNoComponent';

  override showActions: boolean = false;
  override showNoDataFoundMessage: boolean = false;

  queryId: string = '';

  // OU AutoComplete
  ouSuggestions: Option[] = [];
  private readonly ouSubject$ = new Subject<string | undefined>();

  // Cust AutoComplete
  custSuggestions: Option[] = [];
  private readonly custSubject$ = new Subject<string>();

  // Item Number AutoComplete
  itemSuggestions: ItemOption[] = [];
  private readonly itemSubject$ = new Subject<string | undefined>();

  // Currency Dropdown
  currencyOptions: CurrencyOption[] = [];
  selectedCurrency: string = '';

  /** 從 dialog data 帶入的 itemNumber 解析後的 option，供 afterFormDataLoadSuccess 使用 */
  private resolvedItemOption: Option | null = null;

  private resolvedDefaultOu: Option | null = null;

  // Frozen columns
  frozenCount: number = 0;
  frozenWidth = '0px';

  /** 控制 Table 的 DOM 重建，查詢後強制重新渲染凍結欄位位置（同 customer-order-inq 的做法） */
  tableVisible: boolean = true;

  /** 標記本次查詢是否為自動查詢（由 stock-summary 帶參數開啟時觸發） */
  private _isAutoQuery = false;

  /** 凍結欄位：手機版不固定 */
  get effectiveFrozenCount(): number {
    return window.innerWidth <= 768 ? 0 : this.frozenCount;
  }

  @ViewChild('tableComponent') override tableComponent!: Table;

  private readonly cdr = inject(ChangeDetectorRef);

  /** 非凍結欄位（Previous OD + Previous Ship） */
  scrollableColumns: TableColumn[] = [];

  /** Previous OD 欄位 */
  prevOdColumns: TableColumn[] = [];

  /** Previous Ship 欄位 */
  prevShipColumns: TableColumn[] = [];

  constructor(
    private readonly stockSummaryService: StockSummaryService,
    private readonly commonService: CommonService,
    private readonly customerOrderInquiryService: CustomerOrderInquiryService,
    private readonly ucInqByPartNoService: UcInqByPartNoService,
    private readonly authService: AuthService
  ) {
    super();
  }

  override async beforeQuery(): Promise<boolean> {
    return true;
  }

  override async doQuery(): Promise<boolean> {
    const ouValue = this.formControl('ouId').value;
    const custValue = this.formControl('cust').value;
    const itemValue = this.formControl('itemNumber').value;

    const ouId: number = ouValue?.id ? Number(ouValue.id) : 0;
    const custId: number | undefined = custValue?.id ? Number(custValue.id) : undefined;
    const parsedItemId = Number(itemValue?.id);
    const inventoryItemId: number = (itemValue?.id != null && !isNaN(parsedItemId)) ? parsedItemId : 0;
    const empNo: string = this.authService.getUser()?.username ?? '';
    const uuid = crypto.randomUUID();

    const date: number | null = this._isAutoQuery ? 730 : null;
    this._isAutoQuery = false;

    try {
      const request = custId !== undefined
        ? { ouId, empNo, inventoryItemId, custId, uuid, date }
        : { ouId, empNo, inventoryItemId, uuid, date };
      const res = await lastValueFrom(
        this.ucInqByPartNoService.queryUcPartNo(request)
      );
      const currencyFilter = this.formControl('currency').value;
      this.queryId = res.queryId ?? '';
      this.detailDatas = (res.responseData ?? [])
        .filter(row => !currencyFilter || row.currencyCode === currencyFilter)
        .map(row => ({
        ouId: row.orgId,
        ouName: row.ouName,
        custName: row.customerName,
        currency: row.currencyCode,
        prevOdUp: row.orderUnitPrice,
        prevOdTaxCode: row.orderTaxCode,
        prevOdDate: row.orderDate,
        prevOdQty: row.orderQty,
        prevShipUp: row.shipUnitPrice,
        prevShipTaxCode: row.shipTaxCode,
        prevShipDate: row.shipDate,
        prevShipQty: row.shipQty,
      }));
    } catch {
      this.queryId = '';
      this.detailDatas = [];
    }

    this.resetTableLayout();
    return true;
  }

  override searchReset(): void {
    this.queryId = '';
    super.searchReset();
  }

  /**
   * 強制重建 Table DOM，讓 PrimeNG 重新計算凍結欄位的 sticky left 位置
   * 參考 customer-order-inq 的 detailTableVisible 做法
   */
  private resetTableLayout(): void {
    this.tableVisible = false;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.tableVisible = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.updateFrozenWidth();
      }, 0);
    }, 0);
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    return;
  }
  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }
  override initExtraDetailMenu(): void {
    return;
  }
  override setExtraDetailMenuStatus(): void {
    return;
  }

  override initTableColumns(): void {
    this.tableColumns = [
      // Section 1: Frozen columns (no group header)
      { header: 'OU ID', field: 'ouId', sortable: true, frozen: true },
      { header: 'OU Name', field: 'ouName', sortable: true, frozen: true, minWidth: 120 },
      { header: 'Cust. Name', field: 'custName', sortable: true, frozen: true, minWidth: 120 },
      { header: 'Currency', field: 'currency', sortable: true, frozen: true },

      // Section 2: Previous OD
      { header: 'U/P', field: 'prevOdUp', sortable: true, alignRight: true },
      { header: 'Tax Code', field: 'prevOdTaxCode', sortable: true },
      { header: 'Date', field: 'prevOdDate', sortable: true },
      { header: 'Qty', field: 'prevOdQty', sortable: true, numberField: true },

      // Section 3: Previous Ship
      { header: 'U/P', field: 'prevShipUp', sortable: true, alignRight: true },
      { header: 'Tax Code', field: 'prevShipTaxCode', sortable: true },
      { header: 'Date', field: 'prevShipDate', sortable: true },
      { header: 'Qty', field: 'prevShipQty', sortable: true, numberField: true },
    ];

    // Sort frozen columns to front
    const frozenCols = this.tableColumns.filter(c => c.frozen);
    const normalCols = this.tableColumns.filter(c => !c.frozen);
    this.tableColumns = [...frozenCols, ...normalCols];
    this.frozenCount = frozenCols.length;

    // Split scrollable columns into Previous OD / Previous Ship groups
    this.prevOdColumns = normalCols.slice(0, 4);
    this.prevShipColumns = normalCols.slice(4, 8);
    this.scrollableColumns = normalCols;
  }

  override initDialogConfig(dataAction: DataAction): void {
    return;
  }
  override initExtraRowActionMenu(): void {
    return;
  }
  override setExtraRowActionMenuStatus(): void {
    return;
  }
  override doExtraDetailDialogClose(returnData: DialogReturnData): void {
    return;
  }
  override async doRowActionDelete(): Promise<boolean> {
    return true;
  }
  override afterDetailTabActiveItemChange(): void {
    return;
  }

  override initFormGroup(): void {
    this.formGroup = this.formBuilder.nonNullable.group({
      ouId: ['', Validators.required],
      cust: [''],
      currency: [''],
      itemNumber: ['', Validators.required],
      itemDesc: [{ value: '', disabled: true }],
    });
  }

  override initStaticOptions(): void {
    this.loadCurrencyOptions();
  }

  override async initFormData(): Promise<any> {
    const data = this.dynamicDialogConfig.data;
    if (!data?.ou) {
      const orgs = await lastValueFrom(this.stockSummaryService.getOrgs().pipe(take(1))).catch(() => []);
      this.resolvedDefaultOu = orgs[0] ?? null;
    }
    if (data?.itemNumber) {
      const items = await lastValueFrom(
        this.commonService.getItemsWithDesc(data.itemNumber, data.inventoryItemId)
      ).catch(() => []);
      const match = (items as any[]).find((i: any) => i.value === data.itemNumber || i.label === data.itemNumber);
      if (match) {
        this.resolvedItemOption = { id: match.id, value: match.value, label: match.label };
        if (match.description) {
          this.formGroup.patchValue({ itemDesc: match.description });
        }
      }
    }
    return true;
  }

  override afterLangChange(): void {
    return;
  }

  override afterFormDataLoadSuccess(): void {
    const data = this.dynamicDialogConfig.data;

    const patches: any = {};
    if (data?.ou) {
      patches.ouId = data.ou;
    } else if (this.resolvedDefaultOu) {
      patches.ouId = this.resolvedDefaultOu;
    }
    if (this.resolvedItemOption) {
      patches.itemNumber = this.resolvedItemOption;
    } else if (data?.itemNumber) {
      patches.itemNumber = { id: data.inventoryItemId ?? data.itemNumber, value: data.itemNumber, label: data.itemNumber };
    }

    if (Object.keys(patches).length === 0) return;

    this.formGroup.patchValue(patches);
    this.changeDetectorRef.detectChanges();

    // 從 stock-summary 開啟時（同時帶有 ou 與 itemNumber），自動執行一次查詢。
    // 使用 setTimeout(0) 確保在 doLoadFormData 的 finally（hide loading）執行完畢後，
    // 才由 query() 重新接管 loading 的顯示與隱藏，避免 loading 被提早關閉。
    if (data?.ou && data?.itemNumber) {
      this._isAutoQuery = true;
      setTimeout(() => this.query());
    }
  }
  override afterFormDataLoadFail(): void {
    return;
  }
  override initFormControlDisabled(): void {
    return;
  }

  /** Load currency dropdown options from API */
  private async loadCurrencyOptions(): Promise<void> {
    try {
      const currencies = await lastValueFrom(
        this.customerOrderInquiryService.getCurrencies()
      );
      this.currencyOptions = currencies.map(c => ({
        label: c.currencyCode || '',
        value: c.currencyCode || '',
      }));
    } catch {
      this.currencyOptions = [];
    }
  }

  /** Update frozen width after render */
  private updateFrozenWidth(): void {
    if (window.innerWidth <= 768 || !this.tableComponent?.el?.nativeElement) {
      this.frozenWidth = '0px';
      return;
    }
    const frozenHeaders: NodeListOf<Element> = this.tableComponent.el.nativeElement
      .querySelectorAll('thead th.p-frozen-column:not([alignFrozen="right"])');
    let totalWidth = 0;
    frozenHeaders.forEach((th: Element) => {
      totalWidth += (th as HTMLElement).offsetWidth;
    });
    this.frozenWidth = totalWidth > 0 ? `${totalWidth}px` : '0px';
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateFrozenWidth();
  }

  /** Deferred update for frozen width after pagination/sort */
  updateFrozenWidthDeferred(): void {
    setTimeout(() => {
      this.cdr.detectChanges();
      this.recalculateFrozenLeft();
      this.updateFrozenWidth();
    }, 0);
  }

  /**
   * 修正 PrimeNG 換頁或排序後凍結欄位表頭/資料列錯位問題。
   *
   * 問題根源：PrimeNG 僅在欄位「變寬」時主動更新後續凍結欄位的 left 偏移，
   * 「變窄」時不更新，導致後排凍結欄位仍留在舊的（較大）left 位置，出現空隙。
   *
   * 解法：detectChanges 讓 Angular 完成本次 DOM 更新後，
   * 再逐列讀取每個左側凍結欄位的實際 offsetWidth 並重新寫入 left，
   * 確保不論內容變寬或變窄都能正確對齊。
   */
  private recalculateFrozenLeft(): void {
    const tableEl = this.tableComponent?.el?.nativeElement;
    if (!tableEl) { return; }

    const rows: Element[] = Array.from(tableEl.querySelectorAll('tr'));
    rows.forEach(row => {
      let accLeft = 0;
      const leftFrozenCells = (
        Array.from(row.querySelectorAll('th.p-frozen-column, td.p-frozen-column')) as HTMLElement[]
      ).filter(el => {
        const align = el.getAttribute('alignFrozen') ?? el.getAttribute('alignfrozen');
        return align !== 'right';
      });

      leftFrozenCells.forEach(cell => {
        cell.style.left = `${accLeft}px`;
        accLeft += cell.offsetWidth;
      });
    });
  }

  // ===== AutoComplete methods (same pattern as Sales Cost Inquiry) =====

  searchOuSuggestions(event: { query: any }): void {
    if (!this.ouSubject$.observed) {
      this.ouSubject$
        .pipe(
          switchMap((keyword) => this.stockSummaryService.getOrgs(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe((data) => {
          this.ouSuggestions = data;
        });
    }
    this.ouSubject$.next(event.query);
  }

  searchCustSuggestions(event: { query: any }): void {
    if (!this.custSubject$.observed) {
      this.custSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => this.commonService.getCustomersByNumAndName(keyword)),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => { this.custSuggestions = data; },
          error: () => { this.custSuggestions = []; },
        });
    }
    this.custSubject$.next(event.query);
  }

  searchItemSuggestions(event: { query: any }): void {
    if (!this.itemSubject$.observed) {
      this.itemSubject$
        .pipe(
          filter(Boolean),
          switchMap((keyword: string) => {
            const ctrlVal = this.formControl('itemNumber').value;
            const invId: number | undefined = ctrlVal?.id ? Number(ctrlVal.id) : undefined;
            return this.commonService.getItemsWithDesc(keyword, invId);
          }),
          takeUntil(this._destroying$)
        )
        .subscribe({
          next: (data) => { this.itemSuggestions = data; },
          error: () => { this.itemSuggestions = []; },
        });
    }
    this.itemSubject$.next(event.query);
  }

  onItemSelect(event: { value: ItemOption }): void {
    const selected = event.value;
    if (selected?.description) {
      this.formGroup.patchValue({ itemDesc: selected.description });
    } else {
      this.formGroup.patchValue({ itemDesc: '' });
    }
    this.autoCompleteCompleted('autoCompleteCode');
  }

  onItemClear(): void {
    this.formGroup.patchValue({ itemDesc: '' });
    this.autoCompleteCleared('autoCompleteCode');
  }
}
